/**
 * Where a dragged float's position lives.
 *
 * A pin is ring-relative polar, world units: `{ angle, radius }`, unaffected by
 * the camera and rotating with the ring. Because it is ring-relative it
 * survives any layout — which is the whole point of pinning it that way — but
 * an event can be on several rings at once now, so a pin is keyed by
 * `(eventId, tagId)` rather than by event alone (docs/CIRCLES.md §7). `tagId`
 * `null` is the root ring, which the wire carries as `CalEvent.layout`; every
 * other ring's pin arrives in the bundle's `eventPins`.
 *
 * Resolution, once for each ring an event is drawn on:
 *
 *   exact (event, ring) → the root pin → auto
 *
 * Two stores implement the same interface (SPEC, "Pins"):
 *
 *   owner  → RemotePinStore, the server's pins; what viewers see
 *   viewer → LocalPinStore, localStorage only, keyed by calendar id
 *
 * `get` distinguishes "no override" (`undefined`) from "explicitly unpinned
 * here" (`null`), so a viewer can unpin a float the owner pinned without the
 * server pin bleeding back through the `??`.
 */
import { TAU, type CalEvent, type EventPin, type FloatPin } from '@circle/shared';
import { safeStorage } from './api';

export type PinOverride = FloatPin | null | undefined;

/** The root ring's pin, the one the wire carries as `CalEvent.layout`. */
export const ROOT_PIN_TAG: null = null;

/**
 * How a pin is addressed in a flat map: the event id alone for the root ring,
 * `eventId@tagId` for any other. The scene reads these keys directly, and they
 * line up with the entry-id suffix in `core/rings.ts` on purpose.
 */
export function pinKey(eventId: string, tagId: string | null = null): string {
  return tagId === null || tagId === 'root' ? eventId : `${eventId}@${tagId}`;
}

export interface PinStore {
  /** `undefined` = no opinion; `null` = pinned nowhere, i.e. explicitly auto. */
  get(eventId: string, tagId?: string | null): PinOverride;
  /** Two-argument form pins on the root ring; three names the circle's ring. */
  set(eventId: string, pin: FloatPin | null): void;
  set(eventId: string, tagId: string | null, pin: FloatPin | null): void;
  /** Drop every override, wherever the store keeps them. */
  clear(): void;
  /**
   * Drop every override held here without telling anyone else. For a caller
   * that has already reset the server itself — the owner's Reset dialog sends
   * one scoped request and must not be followed by a second, unscoped one.
   */
  forget(): void;
  /** Every override, keyed by {@link pinKey}. */
  all(): Map<string, FloatPin | null>;
}

export function pinsKey(calendarId: string): string {
  return `circle:pins:${calendarId}`;
}

/** Round-trip-safe pin: finite numbers, angle wrapped into [0, 2π). */
export function normalisePin(pin: FloatPin): FloatPin {
  const angle = Number.isFinite(pin.angle) ? pin.angle % TAU : 0;
  return {
    angle: angle < 0 ? angle + TAU : angle,
    radius: Number.isFinite(pin.radius) ? Math.max(0, pin.radius) : 0,
  };
}

function isPin(value: unknown): value is FloatPin {
  if (!value || typeof value !== 'object') return false;
  const p = value as FloatPin;
  return Number.isFinite(p.angle) && Number.isFinite(p.radius);
}

/** `set(id, pin)` and `set(id, tagId, pin)` in one place. */
function readSetArgs(
  a: string | null | FloatPin | undefined,
  b: FloatPin | null | undefined,
  argc: number,
): { tagId: string | null; pin: FloatPin | null } {
  if (argc >= 3) return { tagId: (a as string | null) ?? null, pin: b ?? null };
  return { tagId: null, pin: (a as FloatPin | null) ?? null };
}

/** The stored shape: one record per event, `root` plus a key per circle. */
type StoredPins = Record<string, Record<string, FloatPin | null>>;

/**
 * Viewer mode: overrides in localStorage, never sent anywhere. Reads are
 * tolerant — a corrupt or foreign value is dropped rather than thrown — and
 * they migrate: the flat `{ eventId: pin }` this file wrote before circles
 * existed is read back as that event's root pin, once, and rewritten in the
 * nested shape the next time anything is set.
 */
export class LocalPinStore implements PinStore {
  readonly calendarId: string;
  private readonly storage: Storage | null;
  private cache: Map<string, FloatPin | null>;

  constructor(calendarId: string, storage: Storage | null = safeStorage()) {
    this.calendarId = calendarId;
    this.storage = storage;
    this.cache = this.read();
  }

  get(eventId: string, tagId: string | null = null): PinOverride {
    const key = pinKey(eventId, tagId);
    return this.cache.has(key) ? (this.cache.get(key) ?? null) : undefined;
  }

  set(eventId: string, pin: FloatPin | null): void;
  set(eventId: string, tagId: string | null, pin: FloatPin | null): void;
  set(
    eventId: string,
    a: string | null | FloatPin,
    b?: FloatPin | null,
  ): void {
    const { tagId, pin } = readSetArgs(a, b, arguments.length);
    this.cache.set(pinKey(eventId, tagId), pin ? normalisePin(pin) : null);
    this.write();
  }

  clear(): void {
    this.cache = new Map();
    try {
      this.storage?.removeItem(pinsKey(this.calendarId));
    } catch {
      /* storage unavailable */
    }
  }

  /** There is nobody else to tell: a viewer's pins live here and nowhere else. */
  forget(): void {
    this.clear();
  }

  all(): Map<string, FloatPin | null> {
    return new Map(this.cache);
  }

  private read(): Map<string, FloatPin | null> {
    const out = new Map<string, FloatPin | null>();
    let raw: string | null = null;
    try {
      raw = this.storage?.getItem(pinsKey(this.calendarId)) ?? null;
    } catch {
      return out;
    }
    if (!raw) return out;
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      if (!parsed || typeof parsed !== 'object') return out;
      for (const [eventId, value] of Object.entries(parsed)) {
        // The old flat shape: the value *is* the pin, and it was the root's.
        if (value === null || isPin(value)) {
          out.set(pinKey(eventId), value === null ? null : normalisePin(value));
          continue;
        }
        if (!value || typeof value !== 'object') continue;
        for (const [tagId, pin] of Object.entries(value as Record<string, unknown>)) {
          const key = pinKey(eventId, tagId === 'root' ? null : tagId);
          if (pin === null) out.set(key, null);
          else if (isPin(pin)) out.set(key, normalisePin(pin));
        }
      }
    } catch {
      /* malformed json: start clean */
    }
    return out;
  }

  private write(): void {
    const nested: StoredPins = {};
    for (const [key, pin] of this.cache) {
      const at = key.lastIndexOf('@');
      const eventId = at === -1 ? key : key.slice(0, at);
      const tagId = at === -1 ? 'root' : key.slice(at + 1);
      (nested[eventId] ??= {})[tagId] = pin;
    }
    try {
      this.storage?.setItem(pinsKey(this.calendarId), JSON.stringify(nested));
    } catch {
      /* quota or privacy mode: pins are best-effort */
    }
  }
}

/**
 * The three writes an owner's pins make. `app/api.ts` builds one over its
 * `request`; a test hands in a fake. Kept as an interface so this file, which
 * the scene core imports, never depends on the application's client.
 */
export interface PinApi {
  readonly calendarId: string;
  /** PATCH /api/events/:id — pin or unpin one float on the root ring. */
  setEventLayout(eventId: string, layout: FloatPin | null): Promise<unknown>;
  /** PUT /api/events/:id/pins/:tagId — pin or unpin one float on a circle's ring. */
  setEventPin(eventId: string, tagId: string, pin: FloatPin | null): Promise<unknown>;
  /** POST /api/calendars/:id/layout/reset — clear every owner pin. */
  resetLayout(): Promise<unknown>;
}

export interface RemotePinStoreOptions {
  /** Called when the server rejects a write and the optimistic value is rolled back. */
  onError?: (error: unknown, eventId: string | null) => void;
  /** Called after a rollback so the caller can re-run layout. */
  onRevert?: () => void;
}

/**
 * Owner mode. The root ring's pin still goes to `PATCH /api/events/:id
 * { layout }`, because that is where it has always lived; a circle's ring goes
 * to `PUT /api/events/:id/pins/:tagId`. Both are optimistic — the pin is live
 * before the request lands, and rolled back if it fails.
 */
export class RemotePinStore implements PinStore {
  readonly calendarId: string;
  private readonly api: PinApi;
  private readonly opts: RemotePinStoreOptions;
  private cache = new Map<string, FloatPin | null>();

  constructor(api: PinApi, opts: RemotePinStoreOptions = {}) {
    this.api = api;
    this.calendarId = api.calendarId;
    this.opts = opts;
  }

  get(eventId: string, tagId: string | null = null): PinOverride {
    const key = pinKey(eventId, tagId);
    return this.cache.has(key) ? (this.cache.get(key) ?? null) : undefined;
  }

  set(eventId: string, pin: FloatPin | null): void;
  set(eventId: string, tagId: string | null, pin: FloatPin | null): void;
  set(eventId: string, a: string | null | FloatPin, b?: FloatPin | null): void {
    const { tagId, pin } = readSetArgs(a, b, arguments.length);
    const key = pinKey(eventId, tagId);
    const previous: PinOverride = this.cache.has(key) ? (this.cache.get(key) ?? null) : undefined;
    const next = pin ? normalisePin(pin) : null;
    this.cache.set(key, next);

    const request =
      tagId === null
        ? this.api.setEventLayout(eventId, next)
        : this.api.setEventPin(eventId, tagId, next);

    void request.catch((error: unknown) => {
      if (previous === undefined) this.cache.delete(key);
      else this.cache.set(key, previous);
      this.opts.onError?.(error, eventId);
      this.opts.onRevert?.();
    });
  }

  clear(): void {
    const previous = new Map(this.cache);
    this.cache = new Map();
    void this.api.resetLayout().catch((error: unknown) => {
      this.cache = previous;
      this.opts.onError?.(error, null);
      this.opts.onRevert?.();
    });
  }

  forget(): void {
    this.cache = new Map();
  }

  all(): Map<string, FloatPin | null> {
    return new Map(this.cache);
  }
}

/** Server pins by {@link pinKey}, for the rings that are not the root. */
export function serverPinMap(pins: readonly EventPin[] | undefined): Map<string, FloatPin | null> {
  const out = new Map<string, FloatPin | null>();
  for (const pin of pins ?? []) {
    if (!pin || typeof pin.eventId !== 'string') continue;
    out.set(
      pinKey(pin.eventId, pin.tagId ?? null),
      isPin(pin) ? normalisePin({ angle: pin.angle, radius: pin.radius }) : null,
    );
  }
  return out;
}

/**
 * The pin an event's float takes on one ring: exact `(event, ring)` first,
 * then the root pin, then auto (docs/CIRCLES.md §7). A local override beats the
 * server at each step, and an explicit local `null` stops the search rather
 * than falling through to the level below.
 */
export function effectivePin(
  event: CalEvent,
  store?: PinStore | null,
  tagId: string | null = null,
  serverPins?: ReadonlyMap<string, FloatPin | null>,
): FloatPin | null {
  if (tagId !== null) {
    const override = store?.get(event.id, tagId);
    if (override !== undefined) return override;
    const fromServer = serverPins?.get(pinKey(event.id, tagId));
    if (fromServer !== undefined) return fromServer;
  }
  const rootOverride = store?.get(event.id);
  if (rootOverride !== undefined) return rootOverride;
  return event.layout ?? null;
}

/**
 * The whole effective map, ready for `Scene.setPins` — root pins under the bare
 * event id, per-circle pins under `eventId@tagId`. The scene looks for the
 * ring's key first and falls back to the event's, which is the same resolution
 * order {@link effectivePin} spells out, done once for every ring at a time.
 */
export function effectivePins(
  events: readonly CalEvent[],
  store?: PinStore | null,
  eventPins?: readonly EventPin[],
): Map<string, FloatPin | null> {
  const out = new Map<string, FloatPin | null>();
  const server = serverPinMap(eventPins);
  const known = new Set<string>(store?.all().keys() ?? []);
  for (const key of server.keys()) known.add(key);

  for (const event of events) {
    const root = effectivePin(event, store);
    if (root) out.set(event.id, root);
    for (const key of known) {
      const at = key.lastIndexOf('@');
      if (at === -1 || key.slice(0, at) !== event.id) continue;
      const tagId = key.slice(at + 1);
      const pin = effectivePin(event, store, tagId, server);
      if (pin) out.set(key, pin);
    }
  }
  return out;
}
